// DashboardModals/QuoteDashboardModal.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    FiSun,
    FiZap,
    FiHome,
    FiBattery,
    FiWind,
} from 'react-icons/fi';
import {
    MdElectricalServices,
    MdLocalFireDepartment,
} from 'react-icons/md';
import axios from 'axios';
import SolarCalculatorSection from '../../public/quote/SolarCalculatorSection';
import ElectricalServicesSection from '../../public/quote/ElectricalServicesSection';
import GeneratorServicesSection from '../../public/quote/GeneratorServicesSection';
import ContactInformationSection from '../../public/quote/ContactInformationSection';
import ReviewSection from '../../public/quote/ReviewSection';

const QuoteDashboardModal = ({
    show,
    onClose,
    users,
    API_BASE_URL,
    refetchAll,
    showAlert
}) => {
    const [activeSection, setActiveSection] = useState('selection');
    const [serviceType, setServiceType] = useState(''); // 'solar', 'electrical', 'generator'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [formResetKey, setFormResetKey] = useState(0);
    // Track which steps the user has legitimately reached
    const [completedSections, setCompletedSections] = useState([]);

    const [formData, setFormData] = useState({
        solarData: null,
        electricalData: null,
        generatorData: null,
        contactData: null,
    });

    const [isSolarDataValid, setIsSolarDataValid] = useState(true);

    // ─── Callbacks ────────────────────────────────────────────────────────────
    const handleSolarDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, solarData: data }));
    }, []);

    const handleElectricalDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, electricalData: data }));
    }, []);

    const handleGeneratorDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, generatorData: data }));
    }, []);

    const handleContactDataUpdate = useCallback((data) => {
        setFormData(prev => ({ ...prev, contactData: data }));
    }, []);

    // ─── Pre-seed contact from selected customer ──────────────────────────────
    const contactInitialData = selectedCustomer
        ? {
              name:
                  selectedCustomer.full_name ||
                  `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() ||
                  selectedCustomer.username ||
                  '',
              email: selectedCustomer.email || '',
              phone: selectedCustomer.phone || selectedCustomer.profile?.phone || '',
              address: selectedCustomer.address || selectedCustomer.profile?.address || '',
              preferredContact: 'phone',
              bestTime: 'morning',
              appointmentDate: '',
          }
        : formData.contactData;

    // ─── Reset ────────────────────────────────────────────────────────────────
    const resetForm = useCallback(() => {
        setActiveSection('selection');
        setServiceType('');
        setSelectedCustomer(null);
        setFormData({ solarData: null, electricalData: null, generatorData: null, contactData: null });
        setIsSolarDataValid(true);
        setCompletedSections([]);
        setFormResetKey(prev => prev + 1);
    }, []);

    useEffect(() => {
        if (!show) resetForm();
    }, [show, resetForm]);

    // ─── Section list — mirrors Quote.js exactly ──────────────────────────────
    const getSections = () =>
        serviceType === 'solar'
            ? ['solar', 'contact', 'review']
            : serviceType === 'generator'
            ? ['generator', 'contact', 'review']
            : ['electrical', 'contact', 'review'];

    // ─── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const token = localStorage.getItem('authToken');
        if (!token) {
            showAlert('error', 'Authentication Error', 'Session expired. Please log in again.');
            setIsSubmitting(false);
            return;
        }

        try {
            const submissionData = {
                service_type: serviceType,
                contact_information: formData.contactData || {},
                customer_id: selectedCustomer?.id,
            };

            if (serviceType === 'solar') {
                submissionData.solar_calculator = formData.solarData || {};
            }
            if (serviceType === 'electrical') {
                submissionData.electrical_services = formData.electricalData || {};
            }
            if (serviceType === 'generator') {
                submissionData.generator_services = formData.generatorData || {};
            }
            await axios.post(`${API_BASE_URL}/api/quotations/`, submissionData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            showAlert('success', 'Success!', 'Quotation has been created successfully.');
            if (refetchAll) refetchAll();
            onClose();
            resetForm();
        } catch (error) {
            if (error.response?.status === 400) {
                const errorDetails = error.response?.data;
                let errorMessage = 'Please check your form data and try again.';
                if (typeof errorDetails === 'object') {
                    const errors = Object.entries(errorDetails)
                        .map(([field, messages]) =>
                            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
                        )
                        .join('\n');
                    errorMessage = errors || errorMessage;
                }
                showAlert('error', 'Invalid Form Data', errorMessage, 3000);
            } else {
                showAlert(
                    'error',
                    'Submission Failed',
                    error.response?.data?.message ||
                    error.message ||
                    'An error occurred while submitting the quotation.'
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    // Only show regular users — exclude staff and superusers
    const customers = (users || []).filter(u => !u.is_staff && !u.is_superuser);

    const sections = getSections();
    const isSolarStep = activeSection === 'solar';

    // Navigate forward and mark the current section as completed
    const navigateForward = () => {
        const currentIndex = sections.indexOf(activeSection);
        if (currentIndex < sections.length - 1) {
            const nextSection = sections[currentIndex + 1];
            setCompletedSections(prev =>
                prev.includes(activeSection) ? prev : [...prev, activeSection]
            );
            setActiveSection(nextSection);
        }
    };

    // Step button click — only allow navigating to already-completed sections
    const handleStepClick = (step) => {
        if (completedSections.includes(step) || step === activeSection) {
            setActiveSection(step);
        }
        // Clicking ahead (not yet completed) does nothing
    };

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col">

                    {/* ── Header ───────────────────────────────────────────── */}
                    <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex justify-between items-center rounded-t-xl">
                        <h3 className="text-lg font-semibold text-gray-800">
                            Create New Quotation
                        </h3>
                        <button
                            onClick={() => { onClose(); resetForm(); }}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* ── Body ─────────────────────────────────────────────── */}
                    <div className="p-6">

                        {/* ── Progress Steps ───────────────────────────────── */}
                        <div className="mb-8">
                            <div className="flex justify-between relative">
                                {serviceType ? (
                                    sections.map((step, index, arr) => (
                                        <div key={step} className="flex flex-col items-center z-10">
                                            <button
                                                type="button"
                                                onClick={() => handleStepClick(step)}
                                                disabled={!completedSections.includes(step) && step !== activeSection}
                                                title={
                                                    !completedSections.includes(step) && step !== activeSection
                                                        ? 'Complete the current step first'
                                                        : undefined
                                                }
                                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold transition-all duration-300 ${
                                                    activeSection === step
                                                        ? serviceType === 'electrical'
                                                            ? 'bg-blue-600 scale-110 shadow-lg'
                                                            : serviceType === 'generator'
                                                                ? 'bg-orange-600 scale-110 shadow-lg'
                                                                : 'bg-green-600 scale-110 shadow-lg'
                                                        : completedSections.includes(step)
                                                        ? serviceType === 'electrical'
                                                            ? 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                                                            : serviceType === 'generator'
                                                                ? 'bg-orange-500 hover:bg-orange-600 cursor-pointer'
                                                                : 'bg-green-500 hover:bg-green-600 cursor-pointer'
                                                        : 'bg-gray-300 cursor-not-allowed'
                                                }`}
                                            >
                                                {index + 1}
                                            </button>
                                            <span className="mt-2 text-xs font-medium capitalize text-gray-700">
                                                {step}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center z-10">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold bg-green-600">
                                            1
                                        </div>
                                        <span className="mt-2 text-xs font-medium capitalize text-gray-700">
                                            selection
                                        </span>
                                    </div>
                                )}
                                {/* Progress bar */}
                                {serviceType && (
                                    <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10">
                                        <div
                                            className={`h-full transition-all duration-500 ${serviceType === 'electrical' ? 'bg-blue-500' : serviceType === 'generator' ? 'bg-orange-500' : 'bg-green-500'}`}
                                            style={{
                                                width: `${
                                                    (sections.indexOf(activeSection) / (sections.length - 1)) * 100
                                                }%`,
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Form ─────────────────────────────────────────── */}
                        <form onSubmit={handleSubmit}>

                            {/* ── Step 0: Service Selection + Customer Picker ── */}
                            {activeSection === 'selection' && (
                                <div className="space-y-6">
                                    <div className="text-center mb-6">
                                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                                            Select Customer &amp; Services
                                        </h2>
                                        <p className="text-gray-600">
                                            Choose a customer and the services they're interested in
                                        </p>
                                    </div>

                                    {/* Customer selector */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Customer *
                                        </label>
                                        <select
                                            value={selectedCustomer?.id || ''}
                                            onChange={(e) => {
                                                const customer = users.find(c => c.id === parseInt(e.target.value));
                                                setSelectedCustomer(customer || null);
                                            }}
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        >
                                            <option value="">Choose a customer...</option>
                                            {customers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.full_name ||
                                                        `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                                                        user.username}{' '}
                                                    — {user.email}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Service cards — 4 columns, mirrors Quote.js */}
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

                                        {/* Solar */}
                                        <div
                                            className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                                                serviceType === 'solar'
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-gray-300 hover:border-green-300 hover:shadow-lg'
                                            }`}
                                            onClick={() => setServiceType('solar')}
                                        >
                                            <div className="flex flex-col items-center text-center">
                                                <div className="p-4 bg-green-100 rounded-full mb-4">
                                                    <FiSun className="text-4xl text-green-600" />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800 mb-2">Solar Services</h3>
                                                <p className="text-gray-600 mb-4">Harness the power of the sun</p>
                                                <ul className="text-left space-y-2 mb-6">
                                                    <li className="flex items-start">
                                                        <FiSun className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Complete solar installations</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                        <FiBattery className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Battery backup systems</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                        <MdLocalFireDepartment className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Energy-efficient solutions</span>
                                                    </li>
                                                </ul>
                                                <div className={`px-6 py-2 rounded-full ${
                                                    serviceType === 'solar' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                    Select Solar
                                                </div>
                                            </div>
                                        </div>

                                        {/* Electrical */}
                                        <div
                                            className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                                                serviceType === 'electrical'
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-300 hover:border-blue-300 hover:shadow-lg'
                                            }`}
                                            onClick={() => setServiceType('electrical')}
                                        >
                                            <div className="flex flex-col items-center text-center">
                                                <div className="p-4 bg-blue-100 rounded-full mb-4">
                                                    <FiZap className="text-4xl text-blue-600" />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800 mb-2">Electrical Services</h3>
                                                <p className="text-gray-600 mb-4">Professional electrical solutions</p>
                                                <ul className="text-left space-y-2 mb-6">
                                                    <li className="flex items-start">
                                                        <MdElectricalServices className="text-blue-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Complete rewiring</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                        <FiZap className="text-blue-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Installation &amp; repairs</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                        <FiHome className="text-blue-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Safety inspections</span>
                                                    </li>
                                                </ul>
                                                <div className={`px-6 py-2 rounded-full ${
                                                    serviceType === 'electrical' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                    Select Electrical
                                                </div>
                                            </div>
                                        </div>

                                        {/* Generator */}
                                        <div
                                            className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                                                serviceType === 'generator'
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-300 hover:border-orange-300 hover:shadow-lg'
                                            }`}
                                            onClick={() => setServiceType('generator')}
                                        >
                                            <div className="flex flex-col items-center text-center">
                                                <div className="p-4 bg-orange-100 rounded-full mb-4">
                                                    <FiWind className="text-4xl text-orange-600" />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800 mb-2">Generator Services</h3>
                                                <p className="text-gray-600 mb-4">Reliable backup power solutions</p>
                                                <ul className="text-left space-y-2 mb-6">
                                                    <li className="flex items-start">
                                                        <FiWind className="text-orange-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Generator supply &amp; installation</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                        <FiWind className="text-orange-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Maintenance &amp; servicing</span>
                                                    </li>
                                                    <li className="flex items-start">
                                                        <FiWind className="text-orange-600 mr-2 mt-1 flex-shrink-0" />
                                                        <span>Generator hire &amp; rental</span>
                                                    </li>
                                                </ul>
                                                <div className={`px-6 py-2 rounded-full ${
                                                    serviceType === 'generator' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                    Select Generator
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-center mt-8">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (selectedCustomer && serviceType) {
                                                    setCompletedSections(prev =>
                                                        prev.includes('selection') ? prev : [...prev, 'selection']
                                                    );
                                                    setActiveSection(
                                                        serviceType === 'solar' ? 'solar' :
                                                        serviceType === 'electrical' ? 'electrical' :
                                                        serviceType === 'generator' ? 'generator' : 'solar'
                                                    );
                                                }
                                            }}
                                            disabled={!selectedCustomer || !serviceType}
                                            className={`px-8 py-3 rounded-lg font-semibold transition-colors duration-300 ${
                                                !selectedCustomer || !serviceType
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : serviceType === 'solar'
                                                        ? 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
                                                        : serviceType === 'electrical'
                                                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                                                            : 'bg-orange-600 text-white hover:bg-orange-700 shadow-md hover:shadow-lg'
                                            }`}
                                        >
                                            Continue to{' '}
                                            {serviceType === 'solar' ? 'Solar Calculator' :
                                             serviceType === 'electrical' ? 'Electrical Form' :
                                             serviceType === 'generator' ? 'Generator Form' :
                                             'Quotation Form'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ── Solar Calculator ─────────────────────────── */}
                            {activeSection === 'solar' && (
                                <SolarCalculatorSection
                                    key={`solar-${formResetKey}`}
                                    setIsSolarDataValid={setIsSolarDataValid}
                                    onDataUpdate={handleSolarDataUpdate}
                                    initialData={formData.solarData}
                                />
                            )}

                            {/* ── Electrical Services ──────────────────────── */}
                            {activeSection === 'electrical' && (
                                <ElectricalServicesSection
                                    key={`electrical-${formResetKey}`}
                                    onDataUpdate={handleElectricalDataUpdate}
                                    initialData={formData.electricalData}
                                />
                            )}

                            {/* ── Generator Services ───────────────────────── */}
                            {activeSection === 'generator' && (
                                <GeneratorServicesSection
                                    key={`generator-${formResetKey}`}
                                    onDataUpdate={handleGeneratorDataUpdate}
                                    initialData={formData.generatorData}
                                />
                            )}

                            {/* ── Contact Information ──────────────────────── */}
                            {activeSection === 'contact' && (
                                <ContactInformationSection
                                    key={`contact-${formResetKey}`}
                                    onDataUpdate={handleContactDataUpdate}
                                    initialData={contactInitialData}
                                    overrideUser={selectedCustomer ? {
                                        id: selectedCustomer.id,
                                        username: selectedCustomer.username,
                                        email: selectedCustomer.email || '',
                                        name: selectedCustomer.username,
                                        first_name: selectedCustomer.first_name,
                                        last_name: selectedCustomer.last_name,
                                        full_name:
                                            selectedCustomer.full_name ||
                                            `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() ||
                                            selectedCustomer.username,
                                        profile: {
                                            phone: selectedCustomer.phone || selectedCustomer.profile?.phone || '',
                                            address: selectedCustomer.address || selectedCustomer.profile?.address || '',
                                            company_name: selectedCustomer.profile?.company_name || '',
                                            contact_person: selectedCustomer.profile?.contact_person || '',
                                        }
                                    } : undefined}
                                />
                            )}

                            {/* ── Review & Submit ──────────────────────────── */}
                            {activeSection === 'review' && (
                                <ReviewSection
                                    serviceType={serviceType}
                                    solarData={formData.solarData}
                                    electricalData={formData.electricalData}
                                    generatorData={formData.generatorData}
                                    contactData={formData.contactData}
                                />
                            )}

                            {/* ── Navigation Buttons ───────────────────────── */}
                            {activeSection !== 'selection' && (
                                <div className="flex justify-between border-t border-gray-200 mt-8 pt-6">
                                    <div className="flex space-x-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const currentIndex = sections.indexOf(activeSection);
                                                if (currentIndex > 0) {
                                                    setActiveSection(sections[currentIndex - 1]);
                                                } else {
                                                    setActiveSection('selection');
                                                }
                                            }}
                                            className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-300"
                                            disabled={isSubmitting}
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-6 py-3 border-2 border-red-300 text-red-700 font-semibold rounded-lg hover:bg-red-50 transition-colors duration-300"
                                            disabled={isSubmitting}
                                        >
                                            Reset Form
                                        </button>
                                    </div>

                                    <button
                                        type={activeSection === 'review' ? 'submit' : 'button'}
                                        onClick={(e) => {
                                            if (activeSection !== 'review') {
                                                e.preventDefault();
                                                navigateForward();
                                            }
                                        }}
                                        disabled={
                                            (isSolarStep && (!isSolarDataValid || isSubmitting)) ||
                                            (!isSolarStep && isSubmitting)
                                        }
                                        className={`px-8 py-3 font-semibold rounded-lg transition-colors duration-300 shadow-md ${
                                            (isSolarStep && (!isSolarDataValid || isSubmitting)) ||
                                            (!isSolarStep && isSubmitting)
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : serviceType === 'electrical'
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                                                    : serviceType === 'generator'
                                                        ? 'bg-orange-600 text-white hover:bg-orange-700 hover:shadow-lg'
                                                        : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg'
                                        }`}
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center justify-center">
                                                <svg
                                                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Submitting...
                                            </span>
                                        ) : activeSection === 'review' ? (
                                            'Submit Quotation'
                                        ) : (
                                            'Continue'
                                        )}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuoteDashboardModal;